import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../../components/ui/button';
import ConnectedAccount from '../ConnectedAccount';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../components/ui/dialog';
import { RootState } from '../../../../reducers';
import { getInitialState } from '../../../app/appSlice';
import {
  disconnectGoogleCalendarIntegration,
  initGoogleCalendarIntegration,
} from '../../../../services/integrationService';
import GoogleIcon from '../../../../assets/icons/googlelogo.svg';

const GoogleMeetIntegration = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => ({
    user: state?.settings.user,
  }));

  const [isDisconnectDialogOpen, setIsDisconnectDialogOpen] = useState(false);

  const { isActive, personEmail, personName } =
    user.integrations?.google?.calendar || {};

  const handleConnectGoogleCalendar = async () => {
    const response = await initGoogleCalendarIntegration();
    window.location.href = response.oauthUrl;
  };

  const handleDisconnectGoogleCalendar = async () => {
    setIsDisconnectDialogOpen(false);
    await disconnectGoogleCalendarIntegration();
    dispatch(getInitialState());
  };

  if (!isActive) {
    return (
      <div className="flex items-center gap-4">
        <Button
          variant="minimal"
          onClick={handleConnectGoogleCalendar}
          data-testid="connect-google-calendar-button"
          className="text-black hover:text-black"
        >
          {t('settings.meetingBot.google.connectButton', 'Connect')}
        </Button>
      </div>
    );
  }

  return (
    <>
      <ConnectedAccount
        name={personName}
        email={personEmail}
        onDisconnect={() => setIsDisconnectDialogOpen(true)}
        icon={GoogleIcon}
      />

      <Dialog
        open={isDisconnectDialogOpen}
        onOpenChange={setIsDisconnectDialogOpen}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t(
                'settings.meetingBot.google.disconnectDialogTitle',
                'Disconnect Google Calendar?',
              )}
            </DialogTitle>
            <DialogDescription>
              {t(
                'settings.meetingBot.google.disconnectDialogMessage',
                'This will stop automatic meeting recordings. You can reconnect your calendar at any time.',
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              onClick={() => setIsDisconnectDialogOpen(false)}
              variant="outline"
              className="min-w-[100px]"
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              onClick={handleDisconnectGoogleCalendar}
              variant="destructive"
              className="min-w-[100px]"
            >
              {t('settings.meetingBot.google.disconnect', 'Disconnect')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GoogleMeetIntegration;
